import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { fmtMoney } from '../../utils/formatMoney';
import { useServerPagination } from '../../hooks/useServerPagination';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faCheckCircle, faTimesCircle, faHourglass,
  faBuilding, faFilter, faCoins, faExchangeAlt, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';
const todayBKK = () => dayjs().tz(TZ).format('YYYY-MM-DD');

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const TEXTAREA_CLASS =
  'w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:focus:ring-red-500/30 focus:border-red-500 dark:focus:border-red-500 resize-none';
const LABEL_CLASS =
  'block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2';
const TABLE_ACTION_BTN =
  'w-8 h-8 rounded-lg border transition-colors flex items-center justify-center';

interface SafeWalletTransaction {
  id: string;
  merchant_id: string;
  amount_thb: number;
  fee_rate: number;
  fee_amount: number;
  net_thb: number;
  usdt_rate: number;
  usdt_amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'settled' | 'completed';
  transaction_type: 'deposit' | 'settlement' | 'withdraw';
  settlement_id: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  merchants: { name: string; email: string; company_name: string } | null;
}

const STATUS_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-400',  icon: faHourglass,    label: 'Pending'   },
  approved:  { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  icon: faCheckCircle,  label: 'Approved'  },
  rejected:  { bg: 'bg-red-100 dark:bg-red-900/30',      text: 'text-red-600 dark:text-red-400',      icon: faTimesCircle,  label: 'Rejected'  },
  settled:   { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400', icon: faExchangeAlt,  label: 'Settled'   },
  completed: { bg: 'bg-teal-100 dark:bg-teal-900/30',    text: 'text-teal-700 dark:text-teal-400',    icon: faCheckCircle,  label: 'Completed' },
};

const SafeWalletManagementPage: React.FC = () => {
  const [transactions, setTransactions] = useState<SafeWalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(todayBKK);
  const [dateTo, setDateTo] = useState(todayBKK);
  const [appliedDateFrom, setAppliedDateFrom] = useState(todayBKK);
  const [appliedDateTo, setAppliedDateTo] = useState(todayBKK);

  const [stats, setStats] = useState({
    pending:   { count: 0, amount: 0 },
    approved:  { count: 0, amount: 0 },
    rejected:  { count: 0, amount: 0 },
    settled:   { count: 0, amount: 0 },
    completed: { count: 0, amount: 0 },
  });

  const { currentPage, pageSize, totalItems, setTotalItems, setCurrentPage, setPageSize, serverPaginationProps } = useServerPagination(20);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<SafeWalletTransaction | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);

  const prevStatusRef = useRef(statusFilter);

  const loadStats = useCallback(async () => {
    try {
      const res = await merchantApi.get(`/admin/safe-wallet/stats?start_date=${appliedDateFrom}&end_date=${appliedDateTo}`);
      if (res.data.data) setStats(res.data.data);
    } catch { /* silent */ }
  }, [appliedDateFrom, appliedDateTo]);

  const loadTransactions = useCallback(async (page: number, size: number) => {
    try {
      setLoading(true);
      const offset = (page - 1) * size;
      let url = `/admin/safe-wallet/transactions?limit=${size}&offset=${offset}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (appliedDateFrom) url += `&start_date=${appliedDateFrom}`;
      if (appliedDateTo) url += `&end_date=${appliedDateTo}`;
      const response = await merchantApi.get(url);
      setTransactions(response.data.data || []);
      setTotalItems(response.data.pagination?.total || 0);
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, appliedDateFrom, appliedDateTo, setTotalItems]);

  const reloadAll = useCallback(() => {
    loadTransactions(currentPage, pageSize);
    loadStats();
  }, [loadTransactions, loadStats, currentPage, pageSize]);

  useEffect(() => {
    const statusChanged = prevStatusRef.current !== statusFilter;
    if (statusChanged) {
      prevStatusRef.current = statusFilter;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    loadTransactions(currentPage, pageSize);
    loadStats();
  }, [currentPage, pageSize, statusFilter, appliedDateFrom, appliedDateTo, loadTransactions, loadStats, setCurrentPage]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = todayBKK();
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const openApproveModal = (tx: SafeWalletTransaction) => {
    setSelectedTransaction(tx);
    setShowApproveModal(true);
  };

  const closeApproveModal = () => {
    if (approving) return;
    setShowApproveModal(false);
    setSelectedTransaction(null);
  };

  const handleApprove = async () => {
    if (!selectedTransaction) return;
    try {
      setApproving(true);
      await merchantApi.post(`/admin/safe-wallet/${selectedTransaction.id}/approve`);
      toast.success('อนุมัติรายการสำเร็จ!');
      closeApproveModal();
      reloadAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการอนุมัติ');
    } finally {
      setApproving(false);
    }
  };

  const openRejectModal = (tx: SafeWalletTransaction) => {
    setSelectedTransaction(tx);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    if (submitting) return;
    setShowRejectModal(false);
    setSelectedTransaction(null);
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!selectedTransaction) return;
    if (!rejectReason.trim()) { toast.error('กรุณาระบุเหตุผลในการปฏิเสธ'); return; }
    try {
      setSubmitting(true);
      await merchantApi.post(`/admin/safe-wallet/${selectedTransaction.id}/reject`, {
        reason: rejectReason.trim(),
      });
      toast.success('ปฏิเสธรายการสำเร็จ!');
      closeRejectModal();
      reloadAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการปฏิเสธ');
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'deposit':
        return <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded text-[10px] font-medium">ฝาก Safe</span>;
      case 'settlement':
        return <span className="px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded text-[10px] font-medium">ถอน USDT</span>;
      case 'withdraw':
        return <span className="px-2 py-0.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800 rounded text-[10px] font-medium">คืนเข้า Main</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-slate-600 rounded text-[10px]">{type}</span>;
    }
  };

  const columns: Column<SafeWalletTransaction>[] = useMemo(() => [
    {
      key: 'merchant',
      header: 'Merchant',
      render: (tx) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{tx.merchants?.name || '-'}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{tx.merchants?.email || '-'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'transaction_type',
      header: 'Type',
      render: (tx) => getTypeBadge(tx.transaction_type),
    },
    {
      key: 'amount_thb',
      header: 'Amount (THB)',
      sortable: true,
      render: (tx) => (
        <span className="font-bold text-amber-600 dark:text-amber-400">฿{fmtMoney(tx.amount_thb)}</span>
      ),
    },
    {
      key: 'fee_amount',
      header: 'Fee',
      render: (tx) => (
        <div>
          <p className="text-red-500 dark:text-red-400 text-xs">-{fmtMoney(tx.fee_amount)}</p>
          <p className="text-gray-400 dark:text-slate-500 text-xs">({(tx.fee_rate * 100).toFixed(1)}%)</p>
        </div>
      ),
    },
    {
      key: 'net_thb',
      header: 'Net (THB)',
      sortable: true,
      render: (tx) => (
        <span className="font-semibold text-gray-800 dark:text-slate-100">฿{fmtMoney(tx.net_thb)}</span>
      ),
    },
    {
      key: 'usdt_rate',
      header: 'USDT Rate',
      render: (tx) => (
        <span className="text-gray-600 dark:text-slate-300 text-xs">฿{fmtMoney(tx.usdt_rate)}</span>
      ),
    },
    {
      key: 'usdt_amount',
      header: 'USDT',
      sortable: true,
      render: (tx) => (
        <span className="font-semibold text-green-600 dark:text-green-400 flex items-center gap-1">
          <FontAwesomeIcon icon={faCoins} className="text-[10px]" />
          {tx.usdt_amount.toFixed(2)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (tx) => (
        <div>
          <StatusBadge status={tx.status} statusMap={STATUS_MAP} />
          {tx.rejected_reason && (
            <p className="text-xs text-red-400 dark:text-red-400 mt-1 max-w-[120px] truncate" title={tx.rejected_reason}>
              {tx.rejected_reason}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (tx) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-slate-200">{dayjs(tx.created_at).format('DD/MM/YYYY')}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(tx.created_at).format('HH:mm')}</p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (tx) => (
        <div className="flex items-center justify-center gap-2">
          {tx.status === 'pending' ? (
            <>
              <button
                type="button"
                onClick={() => openApproveModal(tx)}
                className={`${TABLE_ACTION_BTN} bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800`}
                title="อนุมัติ"
              >
                <FontAwesomeIcon icon={faCheckCircle} />
              </button>
              <button
                type="button"
                onClick={() => openRejectModal(tx)}
                className={`${TABLE_ACTION_BTN} bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800`}
                title="ปฏิเสธ"
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600 text-xs">-</span>
          )}
        </div>
      ),
    },
  ], []);

  const statsCards: StatCardDef[] = useMemo(() => [
    {
      icon: faHourglass,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
      label: 'Pending',
      value: <span className="text-amber-600 dark:text-amber-400">{stats.pending.count} <span className="text-sm font-normal text-gray-400 dark:text-slate-500">รายการ</span></span>,
      subValue: <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">฿{fmtMoney(stats.pending.amount)}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'Approved',
      value: <span className="text-green-600 dark:text-green-400">{stats.approved.count} <span className="text-sm font-normal text-gray-400 dark:text-slate-500">รายการ</span></span>,
      subValue: <span className="text-sm font-semibold text-green-600 dark:text-green-400">฿{fmtMoney(stats.approved.amount)}</span>,
    },
    {
      icon: faTimesCircle,
      iconClassName: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'Rejected',
      value: <span className="text-red-600 dark:text-red-400">{stats.rejected.count} <span className="text-sm font-normal text-gray-400 dark:text-slate-500">รายการ</span></span>,
      subValue: <span className="text-sm font-semibold text-red-600 dark:text-red-400">฿{fmtMoney(stats.rejected.amount)}</span>,
    },
    {
      icon: faExchangeAlt,
      iconClassName: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
      label: 'Settled',
      value: <span className="text-indigo-600 dark:text-indigo-400">{stats.settled.count} <span className="text-sm font-normal text-gray-400 dark:text-slate-500">รายการ</span></span>,
      subValue: <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">฿{fmtMoney(stats.settled.amount)}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
      label: 'Withdraw to Main',
      value: <span className="text-teal-600 dark:text-teal-400">{stats.completed.count} <span className="text-sm font-normal text-gray-400 dark:text-slate-500">รายการ</span></span>,
      subValue: <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">฿{fmtMoney(stats.completed.amount)}</span>,
    },
  ], [stats]);

  const txSummary = (tx: SafeWalletTransaction) => (
    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-slate-400">Merchant:</span>
        <strong className="text-gray-800 dark:text-slate-100">{tx.merchants?.name || '-'}</strong>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-slate-400">ยอดเงิน (THB):</span>
        <strong className="text-lg text-amber-600 dark:text-amber-400">฿{fmtMoney(tx.amount_thb)}</strong>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-slate-400">ค่าธรรมเนียม:</span>
        <span className="text-red-500 dark:text-red-400">-฿{fmtMoney(tx.fee_amount)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500 dark:text-slate-400">ยอดสุทธิ (THB):</span>
        <span className="font-semibold text-gray-800 dark:text-slate-100">฿{fmtMoney(tx.net_thb)}</span>
      </div>
      <div className="flex justify-between items-center bg-green-50 dark:bg-green-900/20 rounded-lg px-2 py-1.5 -mx-1">
        <span className="font-semibold text-green-700 dark:text-green-400 flex items-center gap-1">
          <FontAwesomeIcon icon={faCoins} className="text-xs" /> USDT
        </span>
        <span className="font-bold text-green-700 dark:text-green-400">{tx.usdt_amount.toFixed(2)} USDT</span>
      </div>
    </div>
  );

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faShieldAlt}
        title="Safe Wallet Management"
        subtitle={`จัดการรายการ Safe Wallet • ${totalItems.toLocaleString()} รายการ`}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={reloadAll}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      >
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faFilter} className="mr-1" />
            สถานะ
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทั้งหมด</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="settled">Settled</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statsCards} cols={5} />

      <DataTable
        data={transactions}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มีข้อมูล Safe Wallet"
        emptyIcon={<FontAwesomeIcon icon={faShieldAlt} className="text-5xl text-gray-200 dark:text-slate-600" />}
        rowClassName={(tx) => tx.status === 'pending' ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''}
        serverPagination={{
          ...serverPaginationProps,
          pageSizeOptions: [10, 20, 50, 100],
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        hoverable
        compact
      />

      <UiModal
        open={showApproveModal && !!selectedTransaction}
        onClose={closeApproveModal}
        title="ยืนยันอนุมัติรายการ Safe Wallet"
        icon={faCheckCircle}
        iconColor="text-green-500"
        size="md"
        footer={
          selectedTransaction ? (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={closeApproveModal} disabled={approving} className="flex-1 justify-center">
                ยกเลิก
              </UiButton>
              <UiButton
                icon={faCheckCircle}
                onClick={handleApprove}
                disabled={approving}
                loading={approving}
                className="flex-1 justify-center !bg-green-600 hover:!bg-green-700"
              >
                {approving ? 'กำลังอนุมัติ...' : 'ยืนยันอนุมัติ'}
              </UiButton>
            </div>
          ) : undefined
        }
      >
        {selectedTransaction && txSummary(selectedTransaction)}
      </UiModal>

      <UiModal
        open={showRejectModal && !!selectedTransaction}
        onClose={closeRejectModal}
        title="ปฏิเสธรายการ Safe Wallet"
        icon={faTimesCircle}
        iconColor="text-red-500"
        size="md"
        footer={
          selectedTransaction ? (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={closeRejectModal} disabled={submitting} className="flex-1 justify-center">
                ยกเลิก
              </UiButton>
              <UiButton
                variant="danger"
                icon={faTimesCircle}
                onClick={handleReject}
                disabled={!rejectReason.trim() || submitting}
                loading={submitting}
                className="flex-1 justify-center"
              >
                {submitting ? 'กำลังดำเนินการ...' : 'ยืนยันปฏิเสธ'}
              </UiButton>
            </div>
          ) : undefined
        }
      >
        {selectedTransaction && (
          <>
            {txSummary(selectedTransaction)}
            <div>
              <label className={LABEL_CLASS}>
                เหตุผลในการปฏิเสธ <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className={TEXTAREA_CLASS}
                placeholder="ระบุเหตุผลในการปฏิเสธรายการนี้..."
                autoFocus
              />
            </div>
          </>
        )}
      </UiModal>
    </div>
  );
};

export default SafeWalletManagementPage;
